// src/components/ui/LoadingSpinner.jsx
import React from 'react';
import { Loader2 } from 'lucide-react';



export const LoadingSpinner = ({
  message = 'Loading...',
  size = 'md',
  variant = 'default',
  className = '',
  fullScreen = false
}) => {
  // Size mappings
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  // Text size mappings
  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-base',
    lg: 'text-lg',
    xl: 'text-xl'
  };

  // Base spinner component
  const SpinnerIcon = () => (
    <Loader2 
      className={`animate-spin ${sizeClasses[size]} text-indigo-600`}
      style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }}
    />
  );

  // Inline variant - minimal spinner for inline use
  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-2 ${className}`}>
        <SpinnerIcon />
        {message && (
          <span className={`${textSizeClasses[size]} text-gray-600 font-medium`}>
            {message}
          </span>
        )}
      </div>
    );
  }

  // Overlay variant - for overlaying on existing content
  if (variant === 'overlay') {
    return (
      <div className={`absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50 ${className}`}>
        <div className="text-center">
          <SpinnerIcon />
          {message && (
            <p className={`mt-3 ${textSizeClasses[size]} text-gray-600 font-medium`}>
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Full screen variant
  if (fullScreen || variant === 'fullscreen') {
    return (
      <div className={`fixed inset-0 bg-gray-100 flex items-center justify-center z-50 ${className}`}>
        <div className="text-center">
          <div className="mb-4">
            <SpinnerIcon />
          </div>
          {message && (
            <p className={`${textSizeClasses[size]} text-gray-600 font-medium`}>
              {message}
            </p>
          )}
        </div>
      </div>
    );
  }

  // Default variant - centered container
  return (
    <div className={`min-h-screen bg-gray-100 flex items-center justify-center ${className}`}>
      <div className="text-center">
        <div className="mb-4">
          <SpinnerIcon />
        </div>
        {message && (
          <p className={`${textSizeClasses[size]} text-gray-600 font-medium`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

/**
 * Specialized loading spinner for authentication states
 */
export const AuthLoadingSpinner = ({ message = 'Authenticating...', ...props }) => {
  return (
    <LoadingSpinner
      message={message}
      size="lg"
      variant="fullscreen"
      {...props}
    />
  );
};

/**
 * Specialized loading spinner for page loading
 */
export const PageLoadingSpinner = ({ message = 'Loading page...', ...props }) => {
  return (
    <LoadingSpinner
      message={message}
      size="md"
      variant="default"
      {...props}
    />
  );
};

/**
 * Specialized loading spinner for button states
 */
export const ButtonLoadingSpinner = ({ message, ...props }) => {
  return (
    <LoadingSpinner
      message={message}
      size="sm"
      variant="inline"
      {...props}
    />
  );
};

/**
 * Specialized loading spinner for overlay states
 */
export const OverlayLoadingSpinner = ({ message = 'Processing...', ...props }) => {
  return (
    <LoadingSpinner
      message={message}
      size="md"
      variant="overlay"
      {...props}
    />
  );
};
